import { useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import { breadcrumbSchema } from "@/components/Seo";
import Editable from "@/components/Editable";
import EditableSection from "@/components/blocks/EditableSection";
import { Phone, EnvelopeSimple, MapPin, ArrowRight, PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", suburb: "", dog_age: "", enquiry: "", sms_consent: false });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        suburb: form.suburb,
        dog_age: form.dog_age,
        enquiry: form.enquiry + (form.sms_consent ? "\n\n[Customer consents to SMS contact]" : ""),
        source: "contact_form",
      });
      setSent(true);
      toast.success("Thanks — the team will be in touch shortly!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Contact the team | Puppl Sunshine Coast"
        description="Get in touch with the Puppl team for Sunshine Coast puppy training enquiries, bookings, or general questions. Email, phone or use the form."
        canonical={typeof window !== "undefined" ? window.location.origin + "/contact" : ""}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Puppl",
            "url": "https://puppl.app/contact",
            "mainEntity": { "@id": "https://puppl.app/#org" },
          },
          breadcrumbSchema([
            { name: "Home", url: "https://puppl.app/" },
            { name: "Contact", url: "https://puppl.app/contact" },
          ]),
        ]}
      />
      <PublicHeader />
      <EditableSection slug="contact" />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <Editable id="contact.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3" defaultHtml="Get in touch" />
          <Editable id="contact.heading" as="h1" className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-4" defaultHtml="Contact the team." />
          <Editable id="contact.subtitle" as="p" className="text-lg lg:text-xl opacity-90 max-w-2xl" defaultHtml="Got a question, want to book, or just not sure where to start? Drop us a line and the team will be in touch." />
        </div>
      </section>

      <section className="py-20 px-6 lg:px-12 max-w-6xl mx-auto grid lg:grid-cols-3 gap-10">
        <aside className="space-y-4">
          <a href="tel:0435740077" className="card-pp p-5 hover:-translate-y-0.5 transition-transform block">
            <div className="w-10 h-10 rounded-lg bg-[#f38838]/15 flex items-center justify-center mb-3">
              <Phone size={20} weight="duotone" className="text-[#f38838]" />
            </div>
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Phone</div>
            <div className="font-heading font-semibold text-stone-900">0435 740 077</div>
          </a>
          <a href="mailto:support@puppl.app" className="card-pp p-5 hover:-translate-y-0.5 transition-transform block">
            <div className="w-10 h-10 rounded-lg bg-[#1b4a76]/15 flex items-center justify-center mb-3">
              <EnvelopeSimple size={20} weight="duotone" className="text-[#1b4a76]" />
            </div>
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Email</div>
            <div className="font-heading font-semibold text-stone-900 text-sm">support@puppl.app</div>
          </a>
          <div className="card-pp p-5">
            <div className="w-10 h-10 rounded-lg bg-[#E2B054]/20 flex items-center justify-center mb-3">
              <MapPin size={20} weight="duotone" className="text-[#E2B054]" />
            </div>
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Area</div>
            <div className="font-heading font-semibold text-stone-900">Sunshine Coast, QLD</div>
            <div className="text-sm text-stone-500 mt-1">North, Central &amp; Hinterland</div>
          </div>
        </aside>

        <div className="lg:col-span-2 card-pp p-8">
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <PaperPlaneRight size={28} weight="duotone" className="text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">Message sent!</h2>
              <p className="text-stone-600 mb-6">Thanks {form.name.split(" ")[0]} — the team will be in touch shortly.</p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", suburb: "", dog_age: "", enquiry: "", sms_consent: false }); }} className="text-sm font-semibold text-[#f38838] hover:underline">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">Send a message</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" required type="text" placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#f38838]/30" />
                <input data-testid="contact-email" required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#f38838]/30" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <input data-testid="contact-phone" type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#f38838]/30" />
                <input data-testid="contact-suburb" type="text" placeholder="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#f38838]/30" />
                <input data-testid="contact-dog-age" type="text" placeholder="Dog's age" value={form.dog_age} onChange={(e) => setForm({ ...form, dog_age: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#f38838]/30" />
              </div>
              <textarea data-testid="contact-message" required rows={5} placeholder="What can the team help you with? *" value={form.enquiry} onChange={(e) => setForm({ ...form, enquiry: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#f38838]/30" />
              <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  data-testid="contact-sms-consent"
                  type="checkbox"
                  checked={form.sms_consent}
                  onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#f38838]"
                />
                <span>Yes, I'm happy to be contacted by SMS in addition to email. (Optional — you can opt out anytime.)</span>
              </label>
              <button data-testid="contact-submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? "Sending..." : (<>Send Message <ArrowRight size={18} weight="bold" /></>)}
              </button>
            </form>
          )}
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
